import { css, styled } from '@zeiss/beyond-online-react';
import { useBreakpoint } from '@zeiss/lib-helpers';

import { Spacing, StyledContainer } from 'src/types';

interface ButtonGroupProps {
  /**
   * Children
   */
  children?: React.ReactNode;
  /**
   * Spacing to be used as gap between buttons in the group
   *
   * @defaultValue xs
   */
  spacing?: Spacing;
  /**
   * Button group variant
   *
   * The 'default' variant simply groups any number of buttons
   *
   * The 'form' variant should be used for displaying a primary action/cancel pair.
   * On mobile screens the buttons are displayed as column-reverse so that the primary
   * action is displayed before the cancel button
   *
   * @defaultValue default
   */
  variant?: 'default' | 'form';
}

interface ContainerProps extends StyledContainer {
  $spacing: ButtonGroupProps['spacing'];
  $variant: ButtonGroupProps['variant'];
}

const ButtonGroupContainer = styled.div<ContainerProps>`
  display: flex;
  flex-flow: row wrap;
  gap: ${({ $spacing }) => `var(--spacing-${$spacing})`};

  ${({ $variant }) =>
    $variant === 'form' &&
    css`
      flex-direction: column-reverse;
    `};

  ${({ $isMinTablet, $variant }) =>
    $isMinTablet &&
    $variant === 'form' &&
    css`
      flex-direction: row;
    `};
`;

/**
 * Manage spacing between buttons
 *
 * Based on modal button spacing without breakpoint adjustments.
 * @see {@link https://beyond.zeiss.com/d/V6diyrWaeh1V/components#/feedback/modal-composition}
 */
export const ButtonGroup = ({
  children,
  spacing = 'xs',
  variant = 'default',
  ...rest
}: ButtonGroupProps) => {
  const isMinTablet = useBreakpoint('tablet');

  return (
    <ButtonGroupContainer
      $isMinTablet={isMinTablet}
      $spacing={spacing}
      $variant={variant}
      {...rest}
    >
      {children}
    </ButtonGroupContainer>
  );
};
